import { useState, useCallback } from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import RefreshIcon from "@mui/icons-material/Refresh";
import SyncIcon from "@mui/icons-material/Sync";
import PropTypes from "prop-types";
import Swal from "sweetalert2";

import PageHeader from "components/PageHeader";
import DataTable from "components/DataTable";
import FormDialog from "components/FormDialog";
import FormSection from "components/FormSection";

import useBiometricData from "layouts/tables/data/biometricTableData";
import { API_BASE } from "config/api";

function Biometric() {
  const {
    columns,
    rows,
    isLoading,
    error,
    registrationModalOpen,
    setRegistrationModalOpen,
    selectedMember,
    fingerIndex,
    setFingerIndex,
    isProcessing,
    registerFingerprint,
    refresh,
  } = useBiometricData();

  const [isSyncing, setIsSyncing] = useState(false);
  const [syncLabel, setSyncLabel] = useState("Sync Devices");

  const syncDevices = useCallback(async () => {
    const confirmed = await Swal.fire({
      title: "Sync Fingerprint Devices",
      html: "This will copy all member IDs, names and fingerprint templates from <b>Device-1 (192.168.90.13)</b> to <b>Device-2 (192.168.90.12)</b>.<br/><br/>This runs in the background and may take a few minutes.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sync Now",
      cancelButtonText: "Cancel",
    });
    if (!confirmed.isConfirmed) return;

    setIsSyncing(true);
    setSyncLabel("Starting…");

    try {
      const token = localStorage.getItem("authToken");
      const startRes = await window.fetch(`${API_BASE}/biometric/sync-devices`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({}),
      });
      const startData = await startRes.json();

      if (!startRes.ok || startData.status === "error") {
        Swal.fire("Sync Failed", startData.message || "Could not start sync.", "error");
        setIsSyncing(false);
        setSyncLabel("Sync Devices");
        return;
      }

      const jobId = startData.job_id;
      setSyncLabel("Syncing…");

      // Poll every 5 seconds until done or error.
      const poll = async () => {
        try {
          const statusRes = await window.fetch(
            `${API_BASE}/biometric/sync-status?job_id=${encodeURIComponent(jobId)}`,
            { headers: { Accept: "application/json", Authorization: `Bearer ${token}` } }
          );
          const statusData = await statusRes.json();
          const s = statusData.status;

          if (s === "done") {
            Swal.fire(
              "Sync Complete",
              `${statusData.message || "Sync finished."}<br/>` +
                `<b>${statusData.users_ok ?? "?"}</b>/${
                  statusData.users_total ?? "?"
                } members and ` +
                `<b>${statusData.templates_ok ?? "?"}</b>/${
                  statusData.templates_total ?? "?"
                } fingerprint templates copied to Device-2.`,
              "success"
            );
            setIsSyncing(false);
            setSyncLabel("Sync Devices");
          } else if (s === "error") {
            Swal.fire("Sync Failed", statusData.message || "An error occurred.", "error");
            setIsSyncing(false);
            setSyncLabel("Sync Devices");
          } else {
            // still running (started / reading / writing)
            setSyncLabel(s === "reading" ? "Reading…" : s === "writing" ? "Writing…" : "Syncing…");
            setTimeout(poll, 5000);
          }
        } catch {
          // network blip — keep polling
          setTimeout(poll, 5000);
        }
      };

      setTimeout(poll, 5000);
    } catch {
      Swal.fire("Error", "Could not reach the server.", "error");
      setIsSyncing(false);
      setSyncLabel("Sync Devices");
    }
  }, []);

  const FINGER_OPTIONS = [
    { value: 0, label: "Left Thumb" },
    { value: 1, label: "Left Index" },
    { value: 2, label: "Left Middle" },
    { value: 3, label: "Left Ring" },
    { value: 4, label: "Left Pinky" },
    { value: 5, label: "Right Thumb" },
    { value: 6, label: "Right Index" },
    { value: 7, label: "Right Middle" },
    { value: 8, label: "Right Ring" },
    { value: 9, label: "Right Pinky" },
  ];

  if (isLoading) {
    return <Typography sx={{ py: 6 }}>Loading Biometrics...</Typography>;
  }

  if (error) {
    return (
      <Typography color="error" sx={{ py: 6 }}>
        Error: {error}
      </Typography>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Member Biometric Registration"
        actions={
          <Box display="flex" gap={1}>
            <Button
              variant="outlined"
              startIcon={isSyncing ? <CircularProgress size={16} /> : <SyncIcon />}
              onClick={syncDevices}
              disabled={isSyncing}
            >
              {syncLabel}
            </Button>
            <Button variant="contained" startIcon={<RefreshIcon />} onClick={refresh}>
              Refresh
            </Button>
          </Box>
        }
      />

      <Card>
        <DataTable
          table={{ columns, rows }}
          isSorted={false}
          entriesPerPage
          showTotalEntries
          noEndBorder
          canSearch
          searchLabel="Search Members..."
        />
      </Card>

      {/* Fingerprint Registration Modal */}
      <FormDialog
        open={registrationModalOpen}
        onClose={() => setRegistrationModalOpen(false)}
        title="Register Fingerprint"
        onSave={registerFingerprint}
        saveLabel="Enroll Fingerprint"
        saveDisabled={isProcessing}
        loading={isProcessing}
        maxWidth="sm"
      >
        <FormSection>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Registering fingerprint for{" "}
            <strong>
              {selectedMember
                ? selectedMember.first_name && selectedMember.surname
                  ? `${selectedMember.first_name} ${selectedMember.surname}`
                  : selectedMember.memberName || "Selected Member"
                : "Selected Member"}
            </strong>
            . Please select the finger to register and place it on the scanner when prompted.
          </Typography>

          <TextField
            select
            label="Select Finger"
            value={fingerIndex}
            onChange={(e) => setFingerIndex(Number(e.target.value))}
            fullWidth
            margin="normal"
            required
          >
            {FINGER_OPTIONS.map((finger) => (
              <MenuItem key={finger.value} value={finger.value}>
                {finger.label}
              </MenuItem>
            ))}
          </TextField>
        </FormSection>
      </FormDialog>
    </Box>
  );
}

Biometric.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.object),
  rows: PropTypes.arrayOf(PropTypes.object),
  isLoading: PropTypes.bool,
  error: PropTypes.string,
  registrationModalOpen: PropTypes.bool,
  setRegistrationModalOpen: PropTypes.func,
  selectedMember: PropTypes.object,
  fingerIndex: PropTypes.number,
  setFingerIndex: PropTypes.func,
  isProcessing: PropTypes.bool,
  registerFingerprint: PropTypes.func,
  refresh: PropTypes.func,
};

export default Biometric;
